import {
  Flex,
  FormControlSizes,
  isMultileg,
  SymbolSelector,
  SymbolSelectorAppearance,
  useSecuritiesContext,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useMemo } from 'react';
import type { MultilegOrderBookPanelConfig } from '../../../../types/LayoutConfig';
import { Panel } from '../../../Layout/Components/Panel';
import { MultilegOrderBook } from '../../../MultilegCombo/MultilegOrderBook';

export function MultilegOrderBookPanel({ api, params }: IDockviewPanelProps<MultilegOrderBookPanelConfig>) {
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const { symbol, compactOrderBook = false, customTitle } = params;

  const multilegSecurities = useMemo(
    () => searchableSecurities.filter(security => isMultileg(security)),
    [searchableSecurities]
  );

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (!customTitle) {
        api.setTitle(`${symbol ? (securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol) : 'Trading View'} Book`);
      }
      api.updateParameters({ symbol, selectedMarketAccounts: [], selectedMarkets: [] });
    },
    [api, securitiesBySymbol, customTitle]
  );

  const handleCompactClick = useCallback(
    (compactOrderBook: boolean | undefined) => {
      api.updateParameters({ compactOrderBook: !compactOrderBook });
    },
    [api]
  );

  return (
    <Panel panelApi={api} display="flex" flexDirection="column" data-testid="multileg-order-book-panel">
      <Flex overflow="hidden" flex="1 1 0">
        <MultilegOrderBook
          symbol={symbol}
          compactOrderBook={compactOrderBook}
          onCompactClick={handleCompactClick}
          allowCompact={true}
        >
          <SymbolSelector
            appearance={SymbolSelectorAppearance.Ghost}
            size={FormControlSizes.Small}
            symbol={symbol}
            placeholder="Select a symbol"
            onSymbolChanged={handleSymbolChange}
            dropdownWidth="480px"
            portalize={true}
            dropdownPlacement="bottom-start"
            availableSecurities={multilegSecurities}
          />
        </MultilegOrderBook>
      </Flex>
    </Panel>
  );
}
